import { isAxiosError } from 'axios';
import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';
import { useTranslations } from 'next-intl';
import { PropsWithChildren, useState } from 'react';

import { authBunja } from '@flipster/features/src/shared/auth/state';
import { FormattedMessage } from '@flipster/i18n';
import { Button } from '@flipster/react/design-system/button';
import { Modal } from '@flipster/react/design-system/modal/index';
import { RadioButton } from '@flipster/react/design-system/radio-button';
import { snackbar } from '@flipster/react/design-system/snackbar';
import { TextButton } from '@flipster/react/design-system/text-button';
import MinusIcon from '@flipster/react/icon/generated/MinusIcon';
import PlusIcon from '@flipster/react/icon/generated/PlusIcon';
import { css } from '@flipster/styled-system/css';
import { center, flex, hstack, stack } from '@flipster/styled-system/patterns';
import { token } from '@flipster/styled-system/tokens';
import { Slider } from '@flipster/ui';
import { getErrorMessage } from '@flipster/utils/error';

import { getLeverageMarks } from '@web/constants/slider-marker';

import { defaultLeverageMarginModeBunja } from './default-leverage-margin-mode-bunja';

type MarginMode = 'Isolated' | 'Cross';

const defaultMaxLeverage = 50;
const defaultStartLeverage = 1;
const defaultStartMarginMode: MarginMode = 'Isolated';

interface DefaultLeverageMarginModeContentProps {
  onBack: () => void;
  onConfirm?: (params: { leverage: number; marginMode: MarginMode }) => void;
  afterConfirm?: () => void;
  initialLeverage?: number;
  initialMarginMode?: MarginMode;
  originalLeverage?: number;
  originalMarginMode?: MarginMode;
  maxLeverage?: number;
}

export const DefaultLeverageMarginModeContent = ({
  onBack,
  onConfirm,
  afterConfirm,
  initialLeverage,
  initialMarginMode,
  originalLeverage: originalLeverageProp,
  originalMarginMode: originalMarginModeProp,
  maxLeverage = defaultMaxLeverage,
}: DefaultLeverageMarginModeContentProps) => {
  const t = useTranslations();

  const { userAtom } = useBunja(authBunja);
  const user = useAtomValue(userAtom);

  const originalLeverage = originalLeverageProp ?? user?.defaultLeverage ?? defaultStartLeverage;
  const originalMarginMode = originalMarginModeProp ?? user?.defaultMarginType ?? defaultStartMarginMode;

  const [leverage, setLeverage] = useState(() => initialLeverage ?? originalLeverage);
  const [marginMode, setMarginMode] = useState<MarginMode>(() => initialMarginMode ?? originalMarginMode);

  const { putUserTradeDefaultsAtom } = useBunja(defaultLeverageMarginModeBunja);
  const { mutateAsync, isPending } = useAtomValue(putUserTradeDefaultsAtom);

  const isOriginal = leverage === originalLeverage && marginMode === originalMarginMode;
  const isAtResetDefault = leverage === defaultStartLeverage && marginMode === defaultStartMarginMode;

  const handleDecrease = () => {
    if (leverage <= 1) return;
    setLeverage((prev) => prev - 1);
  };

  const handleIncrease = () => {
    if (leverage >= maxLeverage) return;
    setLeverage((prev) => prev + 1);
  };

  const handleReset = () => {
    setLeverage(defaultStartLeverage);
    setMarginMode(defaultStartMarginMode);
  };

  const handleConfirm = async () => {
    try {
      await mutateAsync({ defaultLeverage: leverage, defaultMarginType: marginMode });
      snackbar.success({
        message: t('Trade.r260602.Default_leverage_margin_mode_success_toast', {
          marginMode: t(marginMode === 'Cross' ? 'Trade.r251008.Cross' : 'Trade.r251008.Isolated'),
          leverage: `${leverage}x`,
        }),
      });
      onConfirm?.({ leverage, marginMode });
      afterConfirm?.();
    } catch (error) {
      let message: string;
      if (!isAxiosError(error)) {
        message = t('Common.r250401.Unexpected_error_message');
      } else if (!error.response) {
        message = t('Common.r260224.Network_error_toast');
      } else {
        message = getErrorMessage(error);
      }
      snackbar.error({ message });
    }
  };

  return (
    <>
      <Modal.Header>
        <Modal.Title label={<FormattedMessage id='Trade.r260528.Default_leverage_margin_mode' />} />
      </Modal.Header>
      <Modal.Content>
        <div className={stack({ gap: 24 })}>
          <section className={stack({ gap: 12 })}>
            <h3 className={css({ textStyle: 'Body/M/Strong', color: 'content.default.level-5' })}>
              <FormattedMessage id='Trade.r260528.Default_leverage' />
            </h3>
            <div
              className={stack({
                gap: 24,
                p: 16,
                borderRadius: 12,
                borderWidth: '1px',
                borderStyle: 'solid',
                borderColor: 'border.default.level-1',
              })}
            >
              <div className={flex({ justify: 'space-between', align: 'center' })}>
                <span className={css({ textStyle: 'Title/M/Monospace', color: 'content.default.level-5' })}>
                  {leverage}x
                </span>
                <div className={flex({ gap: 8 })}>
                  <button type='button' className={marginModeButtonStyle} onClick={handleDecrease}>
                    <MinusIcon size={16} color={token('colors.content.default.level-4')} />
                  </button>
                  <button type='button' className={marginModeButtonStyle} onClick={handleIncrease}>
                    <PlusIcon size={16} color={token('colors.content.default.level-4')} />
                  </button>
                </div>
              </div>
              <Slider
                value={leverage}
                min={1}
                max={maxLeverage}
                marks={getLeverageMarks(maxLeverage)}
                onChange={setLeverage}
                hierarchy='secondary'
                marksStyle={css({ textStyle: 'Text/S/Monospace/Regular', color: 'content.default.level-2' })}
              />
            </div>
          </section>
          <section className={stack({ gap: 12 })}>
            <h3 className={css({ textStyle: 'Body/L/Bold', color: 'content.default.level-5' })}>
              <FormattedMessage id='Trade.r260528.Default_margin_mode' />
            </h3>
            <div className={flex({ gap: 8 })}>
              <MarginModeOption isSelected={marginMode === 'Isolated'} onSelect={() => setMarginMode('Isolated')}>
                <RadioButton
                  name='defaultMarginMode'
                  value='Isolated'
                  checked={marginMode === 'Isolated'}
                  onChange={() => setMarginMode('Isolated')}
                  css={{ color: 'content.default.level-5', borderColor: 'border.default.level-5' }}
                >
                  <FormattedMessage id='Trade.r251008.Isolated' />
                </RadioButton>
              </MarginModeOption>
              <MarginModeOption isSelected={marginMode === 'Cross'} onSelect={() => setMarginMode('Cross')}>
                <RadioButton
                  name='defaultMarginMode'
                  value='Cross'
                  checked={marginMode === 'Cross'}
                  onChange={() => setMarginMode('Cross')}
                  css={{ color: 'content.default.level-5' }}
                >
                  <FormattedMessage id='Trade.r251008.Cross' />
                </RadioButton>
              </MarginModeOption>
            </div>
          </section>

          <ul
            className={css({
              list: 'disc',
              textStyle: 'Body/XS/Regular',
              color: 'content.default.level-2',
              '& > li + li': { mt: 4 },
            })}
          >
            <li>
              <FormattedMessage id='Trade.r260528.Default_leverage_margin_mode_disclaimer_1' />
            </li>
            <li>
              <FormattedMessage id='Trade.r260528.Default_leverage_margin_mode_disclaimer_2' />
            </li>
            <li>
              <FormattedMessage id='Trade.r260528.Default_leverage_margin_mode_disclaimer_3' />
            </li>
            <li>
              <FormattedMessage id='Trade.r260528.Default_leverage_margin_mode_disclaimer_4' />
            </li>
          </ul>
          <div className={center()}>
            <TextButton
              size='medium'
              color='neutral'
              disabled={isAtResetDefault}
              label={<FormattedMessage id='Trade.r260528.Reset_to_original_isolated_1x' />}
              onClick={handleReset}
            />
          </div>
        </div>
      </Modal.Content>
      <Modal.Footer>
        <div className={hstack({ gap: 8, w: '100%' })}>
          <Button
            size='large'
            hierarchy='secondary'
            color='neutral'
            label={<FormattedMessage id='Common.r250401.Back' />}
            onClick={onBack}
            disabled={isPending}
            css={{ flex: 1 }}
          />
          <Button
            size='large'
            label={<FormattedMessage id='Common.r250401.Confirm' />}
            disabled={isOriginal}
            isLoading={isPending}
            withSpinner
            onClick={handleConfirm}
            css={{ flex: 1 }}
          />
        </div>
      </Modal.Footer>
    </>
  );
};

const marginModeButtonStyle = flex({
  align: 'center',
  justify: 'center',
  width: '32px',
  height: '32px',
  borderRadius: '4px',
  bg: 'background.default.level-2',
});

const MarginModeOption = ({
  children,
  isSelected,
  onSelect,
}: PropsWithChildren<{ isSelected: boolean; onSelect: () => void }>) => {
  return (
    <div
      onClick={onSelect}
      className={flex({
        flex: 1,
        align: 'center',
        gap: 8,
        borderRadius: 12,
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor: isSelected ? 'border.default.level-5' : 'border.default.level-1',
        py: 12,
        px: 16,
        cursor: 'pointer',
      })}
    >
      {children}
    </div>
  );
};
